import type { BadgeProps } from "@bowfast/ui/components/badge";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  ClockIcon,
  CompassIcon,
  CreditCardIcon,
  FileTextIcon,
  MoreHorizontalIcon,
  Navigation2Icon,
  PenLineIcon,
  PlayCircleIcon,
  ShareIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { CrewTab } from "~/components/expeditions/CrewTab";
import { DocumentsTab } from "~/components/expeditions/DocumentsTab";
import { ExpensesTab } from "~/components/expeditions/ExpensesTab";
import { LogTab } from "~/components/expeditions/LogTab";
import { OverviewTab } from "~/components/expeditions/OverviewTab";
import { RouteTab } from "~/components/expeditions/RouteTab";
import { DetailPageSkeleton } from "~/components/shared/DetailPageSkeleton";
import { ListLoadError } from "~/components/shared/ListLoadError";
import type {
  AddLogbookEntryInput,
  CrewRole,
  ExpeditionStatus,
  RecordExpenseInput,
  VesselPingsSubscription,
} from "~/graphql/generated";
import { fadeUp } from "~/lib/animations";
import { KIND_LABEL, STATUS_LABEL } from "~/lib/expedition-constants";
import { fmtDays, fmtPrice, fmtRelativeTime } from "~/lib/fmt";
import { createSdk } from "~/lib/gql";
import { createWsClient } from "~/lib/gql-ws";
import {
  type ActivityEntry,
  type DocumentEntry,
  EXPEDITION as EXPEDITION_MOCK,
  STATUS_GRADIENT_REAL,
} from "~/pages/expeditions/expedition-detail.mock";
import { Route } from "~/routes/_authenticated/expeditions.$expeditionSlug";
import type { DetailTabValue } from "./expeditions.types";

// ── Status helpers ────────────────────────────────────────────────────────────

function statusBadgeVariant(s: ExpeditionStatus): BadgeProps["variant"] {
  if (s === "ACTIVE") return "active";
  if (s === "OPEN") return "open";
  if (s === "COMPLETED") return "completed";
  if (s === "CANCELLED") return "cancelled";
  return "planning";
}

// ── Live position banner ──────────────────────────────────────────────────────

interface LiveTracking {
  position: string;
  speed: string;
  heading: string;
  // TODO(meteo): replace `wind` random value with real data from OpenMeteo /
  // VisualCrossing / yr.no once a meteo integration is implemented.
  wind: string;
  lastPing: string;
  vessel: string;
  isSimulated?: boolean;
}

function LiveBanner({ tracking }: Readonly<{ tracking: LiveTracking }>) {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}>
      <div className="flex flex-wrap items-center gap-4 rounded-xl bg-linear-to-br from-emerald-950 to-emerald-900 border border-emerald-800 px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="relative flex size-2.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-emerald-300">
            Live position
            {tracking.isSimulated && (
              <span className="ml-1.5 rounded bg-emerald-800/60 px-1.5 py-0.5 text-[0.5625rem] text-emerald-400/80">
                SIM
              </span>
            )}
          </span>
        </div>
        <TelemetryCell label="Position" value={tracking.position} />
        <TelemetrySep />
        <TelemetryCell label="Speed" value={tracking.speed} />
        <TelemetrySep />
        <TelemetryCell label="Heading" value={tracking.heading} />
        <TelemetrySep />
        <TelemetryCell label="Wind" value={tracking.wind} />
        <TelemetrySep />
        <TelemetryCell label="Last ping" value={tracking.lastPing} />
        <div className="ml-auto hidden text-xs text-emerald-400/60 lg:block">
          {tracking.isSimulated ? "Simulated · no AIS feed" : `Tracking via AIS · ${tracking.vessel}`}
        </div>
      </div>
    </motion.div>
  );
}

function TelemetryCell({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <p className="text-[0.5625rem] font-bold uppercase tracking-[0.08em] text-emerald-400/70">{label}</p>
      <p className="font-heading text-sm font-semibold tabular-nums text-emerald-50">{value}</p>
    </div>
  );
}

function TelemetrySep() {
  return <div className="hidden h-8 w-px bg-emerald-800 sm:block" aria-hidden />;
}

// ── Hero KPI strip ────────────────────────────────────────────────────────────

function HeroKpi({
  label,
  value,
  sub,
  highlight,
}: Readonly<{ label: string; value: string; sub: string; highlight?: "primary" | "success" }>) {
  return (
    <div className="flex flex-1 flex-col gap-0.5 min-w-[6.5rem] px-5 py-3.5 border-r border-border last:border-r-0">
      <span className="text-[0.625rem] font-bold uppercase tracking-[0.07em] text-muted-foreground">{label}</span>
      <span
        className={cn(
          "font-heading text-[1.375rem] font-bold tabular-nums leading-none",
          highlight === "primary" && "text-abyss-500",
          highlight === "success" && "text-shallow-600",
          !highlight && "text-foreground",
        )}
      >
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </div>
  );
}

// ── Status CTA ────────────────────────────────────────────────────────────────

function StatusActions({
  status,
  onAdvance,
  isMutating,
}: Readonly<{
  status: ExpeditionStatus;
  onAdvance: (next: ExpeditionStatus) => void;
  isMutating: boolean;
}>) {
  if (status === "PLANNING") {
    return (
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="gap-2">
          <PenLineIcon className="size-3.5" />
          Edit
        </Button>
        <Button size="sm" className="gap-2" disabled={isMutating} onClick={() => onAdvance("OPEN")}>
          <PlayCircleIcon className="size-3.5" />
          Open for applications
        </Button>
      </div>
    );
  }
  if (status === "OPEN") {
    return (
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="gap-2">
          <UserPlusIcon className="size-3.5" />
          Invite crew
        </Button>
        <Button size="sm" className="gap-2" disabled={isMutating} onClick={() => onAdvance("ACTIVE")}>
          <PlayCircleIcon className="size-3.5" />
          Start expedition
        </Button>
      </div>
    );
  }
  if (status === "FULLY_BOOKED") {
    return (
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="gap-2">
          <UsersIcon className="size-3.5" />
          Manage crew
        </Button>
        <Button size="sm" disabled className="gap-2">
          <PlayCircleIcon className="size-3.5" />
          Start expedition
        </Button>
      </div>
    );
  }
  if (status === "ACTIVE") {
    return (
      <div className="flex gap-2">
        <Button size="sm" className="gap-2" disabled={isMutating} onClick={() => onAdvance("COMPLETED")}>
          <PlayCircleIcon className="size-3.5" />
          Complete voyage
        </Button>
        <Button size="sm" variant="outline" className="gap-2">
          <PenLineIcon className="size-3.5" />
          Edit
        </Button>
        <Button size="sm" variant="outline" className="gap-2">
          <ShareIcon className="size-3.5" />
          Share
        </Button>
        <Button variant="ghost" size="icon-sm">
          <MoreHorizontalIcon className="size-4" />
        </Button>
      </div>
    );
  }
  return null;
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TABS = [
  { value: "overview", label: "Overview", icon: CompassIcon },
  { value: "route", label: "Route", icon: Navigation2Icon },
  { value: "crew", label: "Crew", icon: UsersIcon },
  { value: "expenses", label: "Expenses", icon: CreditCardIcon },
  { value: "documents", label: "Documents", icon: FileTextIcon },
  { value: "log", label: "Log", icon: ClockIcon },
] as const satisfies Array<{ value: DetailTabValue; label: string; icon: React.ElementType }>;

// Mock documents — TODO(backend/documents): replace with real DocumentsList query.
const MOCK_DOCUMENTS: DocumentEntry[] = EXPEDITION_MOCK.documents;

// Mock activity — TODO(backend/activity): replace with real ActivityFeed query.
const MOCK_ACTIVITY: ActivityEntry[] = EXPEDITION_MOCK.activity;

// Format lat/lng numbers as a compact position string.
function fmtPosition(lat: number, lng: number): string {
  const latDir = lat >= 0 ? "N" : "S";
  const lngDir = lng >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(3)}°${latDir} ${Math.abs(lng).toFixed(3)}°${lngDir}`;
}

export function ExpeditionDetailPage() {
  const { expeditionSlug } = Route.useParams();
  const { user } = useAuth();
  const token = user?.access_token ?? "";
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<DetailTabValue>("overview");
  const [liveTracking, setLiveTracking] = useState<LiveTracking | null>(null);

  // Random wind frozen on mount for the duration of the session.
  // TODO(meteo): replace with real wind data from OpenMeteo / VisualCrossing
  // once a weather integration is implemented. The VesselPing type would also
  // need `windSpeedKnots` and `windDirection` fields added to the tracking subgraph.
  const windKnots = useRef(Math.floor(Math.random() * 21) + 5);

  // ── Primary query ───────────────────────────────────────────────────────────
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["expedition", expeditionSlug],
    queryFn: () => createSdk(token).ExpeditionBySlug({ slug: expeditionSlug }),
    enabled: !!token,
  });

  const exp = data?.expeditionBySlug;
  const expId = exp?.id;
  const isActive = exp?.status === "ACTIVE";

  // ── Expenses query ──────────────────────────────────────────────────────────
  const { data: expData } = useQuery({
    queryKey: ["expedition-expenses", expId],
    // biome-ignore lint/style/noNonNullAssertion: enabled guard ensures expId is defined
    queryFn: () => createSdk(token).ExpeditionExpensesList({ expeditionId: expId! }),
    enabled: !!expId && !!token,
  });
  const expenses = expData?.expenses ?? [];

  // ── Logbook query ───────────────────────────────────────────────────────────
  const { data: logData } = useQuery({
    queryKey: ["logbook", expId],
    // biome-ignore lint/style/noNonNullAssertion: enabled guard ensures expId is defined
    queryFn: () => createSdk(token).LogbookEntries({ expeditionId: expId! }),
    enabled: !!expId && !!token,
  });
  const logbookEntries = logData?.logbookEntries ?? [];

  // ── Status mutation ─────────────────────────────────────────────────────────
  const updateStatus = useMutation({
    mutationFn: (status: ExpeditionStatus) =>
      // biome-ignore lint/style/noNonNullAssertion: expId is defined when mutation is callable
      createSdk(token).UpdateExpeditionStatus({ id: expId!, status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition", expeditionSlug] }),
  });

  // ── Crew mutations ──────────────────────────────────────────────────────────
  const approveCrewMember = useMutation({
    mutationFn: (membershipId: string) => createSdk(token).ApproveCrewMember({ membershipId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition", expeditionSlug] }),
  });

  const declineCrewMember = useMutation({
    mutationFn: (membershipId: string) => createSdk(token).DeclineCrewMember({ membershipId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition", expeditionSlug] }),
  });

  const removeCrewMember = useMutation({
    mutationFn: (membershipId: string) => createSdk(token).RemoveCrewMember({ membershipId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition", expeditionSlug] }),
  });

  const updateCrewRole = useMutation({
    mutationFn: ({ membershipId, role }: { membershipId: string; role: CrewRole }) =>
      createSdk(token).UpdateCrewMembershipRole({ membershipId, role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition", expeditionSlug] }),
  });

  // ── Expense mutations ───────────────────────────────────────────────────────
  const recordExpense = useMutation({
    mutationFn: (input: RecordExpenseInput) =>
      // biome-ignore lint/style/noNonNullAssertion: expId is defined when mutation is callable
      createSdk(token).RecordExpense({ expeditionId: expId!, input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition-expenses", expId] }),
  });

  const deleteExpense = useMutation({
    mutationFn: (id: string) => createSdk(token).DeleteExpense({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition-expenses", expId] }),
  });

  const settleExpense = useMutation({
    mutationFn: (expenseId: string) =>
      // biome-ignore lint/style/noNonNullAssertion: expId is defined when mutation is callable
      createSdk(token).SettleExpense({ expeditionId: expId!, input: { expenseId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expedition-expenses", expId] }),
  });

  // ── Logbook mutations ───────────────────────────────────────────────────────
  const addLogbookEntry = useMutation({
    mutationFn: (input: AddLogbookEntryInput) =>
      // biome-ignore lint/style/noNonNullAssertion: expId is defined when mutation is callable
      createSdk(token).AddLogbookEntry({ expeditionId: expId!, input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["logbook", expId] }),
  });

  const deleteLogbookEntry = useMutation({
    mutationFn: (id: string) => createSdk(token).DeleteLogbookEntry({ id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["logbook", expId] }),
  });

  // ── VesselPings subscription ────────────────────────────────────────────────
  const hasRealPingRef = useRef(false);

  useEffect(() => {
    if (!token || !expId || !isActive) return;

    const vesselMap = new Map((exp?.vessels ?? []).map((v) => [v.vessel.id, v.vessel.name]));

    const client = createWsClient(token);

    const unsubscribe = client.subscribe(
      {
        query: `subscription VesselPings($expeditionId: ID!) {
  vesselPings(expeditionId: $expeditionId) {
    expeditionId vesselId lat lng speedKnots heading altitudeM time
  }
}`,
        variables: { expeditionId: expId },
      },
      {
        next: (event) => {
          if (!event.data) return;
          const ping = (event.data as VesselPingsSubscription).vesselPings;
          hasRealPingRef.current = true;
          setLiveTracking({
            position: fmtPosition(ping.lat, ping.lng),
            speed: ping.speedKnots != null ? `${ping.speedKnots.toFixed(1)} kt` : "—",
            heading: ping.heading != null ? `${ping.heading}°` : "—",
            wind: `${windKnots.current} kt`,
            lastPing: fmtRelativeTime(ping.time),
            vessel: vesselMap.get(ping.vesselId) ?? ping.vesselId,
            isSimulated: false,
          });
        },
        error: () => {
          // Silently degrade — live tracking is best-effort.
        },
        complete: () => {},
      },
    );

    return () => {
      unsubscribe();
      void client.dispose();
    };
  }, [token, expId, isActive, exp?.vessels]);

  // ── Simulated vessel position ───────────────────────────────────────────────
  // When the expedition is active but no real AIS/GPS pings arrive, we generate
  // a simulated position so the LiveBanner and tracking UX can be exercised
  // during development and demos.
  //
  // Simulation strategy:
  //  - Start from the first route waypoint (or 38°N 15°E — Strait of Messina).
  //  - Tick every 5 s with a small random drift (~100 m per step).
  //  - Real WS pings (hasRealPingRef) always override the simulation.

  useEffect(() => {
    if (!isActive || !expId) return;

    const firstWp = exp?.route?.waypoints[0];
    const vesselName = exp?.vessels[0]?.vessel.name ?? "—";

    // Mutable simulation state kept in closure — no React state needed.
    let lat = firstWp?.lat ?? 38.0;
    let lng = firstWp?.lng ?? 15.0;
    let heading = Math.floor(Math.random() * 360);

    // Show an initial ping immediately without waiting for the first interval.
    if (!hasRealPingRef.current) {
      const speed = (6 + Math.random() * 4).toFixed(1);
      setLiveTracking({
        position: fmtPosition(lat, lng),
        speed: `${speed} kt`,
        heading: `${heading}°`,
        wind: `${windKnots.current} kt`,
        lastPing: "Just now",
        vessel: vesselName,
        isSimulated: true,
      });
    }

    const interval = setInterval(() => {
      if (hasRealPingRef.current) return; // real pings have taken over — don't overwrite

      // ~100–200 m drift each tick
      lat += (Math.random() - 0.5) * 0.002;
      lng += (Math.random() - 0.5) * 0.003;
      heading = (heading + Math.floor((Math.random() - 0.5) * 10) + 360) % 360;
      const speed = (6 + Math.random() * 4).toFixed(1);

      setLiveTracking({
        position: fmtPosition(lat, lng),
        speed: `${speed} kt`,
        heading: `${heading}°`,
        wind: `${windKnots.current} kt`,
        lastPing: "Just now",
        vessel: vesselName,
        isSimulated: true,
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isActive, expId, exp?.route?.waypoints, exp?.vessels]);

  // ── Loading / error / not found ─────────────────────────────────────────────
  if (isPending) return <DetailPageSkeleton statCount={4} />;
  if (isError) {
    return (
      <ListLoadError
        title="Couldn't load this expedition"
        description="Check your connection and try again."
        onRetry={refetch}
      />
    );
  }
  if (!exp) {
    return (
      <div className="flex flex-1 items-center justify-center p-12 text-sm text-muted-foreground">
        Expedition not found.
      </div>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────────
  const gradient = STATUS_GRADIENT_REAL[exp.status];
  const confirmedCrewCount = exp.crew.filter((m) => m.status === "CONFIRMED").length;
  const duration = fmtDays(exp.departureDate, exp.returnDate);
  const distanceNm = exp.route?.totalDistanceNm ?? 0;
  const primaryVessel = exp.vessels[0]?.vessel;
  const currency = expenses[0]?.currency ?? "EUR";
  const totalExpenses = expenses.reduce((s, e) => s + e.amountCents / 100, 0);
  const unsettledCount = expenses.filter(
    (e) => e.splits.length > 0 && !e.splits.every((s) => s.settledAt != null),
  ).length;

  function tabCount(value: DetailTabValue): number {
    if (value === "crew") return confirmedCrewCount;
    if (value === "expenses") return expenses.length;
    if (value === "route") return exp?.route?.waypoints.length ?? 0;
    if (value === "log") return logbookEntries.length;
    if (value === "documents") return MOCK_DOCUMENTS.length;
    return 0;
  }
  function tabWarning(value: DetailTabValue): number {
    if (value === "expenses") return unsettledCount;
    return 0;
  }

  const statusMutating = updateStatus.isPending;
  const crewMutating =
    approveCrewMember.isPending ||
    declineCrewMember.isPending ||
    removeCrewMember.isPending ||
    updateCrewRole.isPending;
  const expenseMutating = recordExpense.isPending || deleteExpense.isPending || settleExpense.isPending;
  const logMutating = addLogbookEntry.isPending || deleteLogbookEntry.isPending;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-5 p-4 md:p-6">
      {/* Back nav */}
      <motion.div {...fadeUp}>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1.5 text-muted-foreground"
          nativeButton={false}
          render={<Link to="/expeditions" />}
        >
          <ArrowLeftIcon className="size-3.5" />
          Expeditions
        </Button>
      </motion.div>

      {/* ── Hero card ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
        <Card className="overflow-hidden">
          <CardContent className="px-6 py-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-6">
              <div
                className={cn(
                  "flex size-20 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br p-0.75",
                  gradient,
                )}
              >
                <div className="flex size-full items-center justify-center rounded-[13px] bg-abyss-900">
                  <CompassIcon className="size-8 text-white/80" />
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="font-heading text-xl font-bold tracking-tight md:text-2xl">{exp.name}</h1>
                    <Badge
                      variant={statusBadgeVariant(exp.status)}
                      dotted={isActive || exp.status === "OPEN"}
                      pulse={isActive}
                      className="text-[0.6875rem]"
                    >
                      {STATUS_LABEL[exp.status]}
                    </Badge>
                    {exp.kind && (
                      <Badge variant="outline" className="text-[0.6875rem]">
                        {KIND_LABEL[exp.kind]}
                      </Badge>
                    )}
                    {/* TODO(backend/expedition): add `dayNumber` + `totalDays` for "Day X of Y" badge */}
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {/* TODO(backend/vessels): add `flag` emoji to Vessel type */}
                    {primaryVessel?.name ?? "—"} · {primaryVessel?.homePort ?? "—"}
                  </p>
                  {isActive && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-shallow-600 dark:text-shallow-400">
                      <span className="relative flex size-1.5">
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-500 opacity-60" />
                        <span className="relative inline-flex size-1.5 rounded-full bg-shallow-500" />
                      </span>
                      At sea now
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-start gap-2">
                  <StatusActions
                    status={exp.status}
                    onAdvance={(next) => updateStatus.mutate(next)}
                    isMutating={statusMutating}
                  />
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex-wrap gap-0 p-0">
            <HeroKpi
              label="Distance"
              value={distanceNm > 0 ? `${distanceNm.toLocaleString()} nm` : "—"}
              sub="route distance"
            />
            {/* TODO(backend/tracking): add `coveredNm` KPI once VesselPings history is available */}
            <HeroKpi label="Crew" value={`${confirmedCrewCount} / ${exp.maxCrew}`} sub="berths filled" />
            <HeroKpi
              label="Expenses"
              value={expenses.length > 0 ? fmtPrice(totalExpenses, currency) : "—"}
              sub={expenses.length > 0 ? `${expenses.length} logged` : "none logged"}
            />
            {/* TODO(backend/expedition): add `budget` field for "of €X budget" sub label */}
            <HeroKpi label="Duration" value={duration} sub="planned passage" />
          </CardFooter>
        </Card>
      </motion.div>

      {/* ── Live position banner (active only) ── */}
      {isActive && liveTracking && <LiveBanner tracking={liveTracking} />}

      {/* Unsettled expenses warning */}
      {exp.status === "COMPLETED" && unsettledCount > 0 && (
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }}>
          <div className="flex items-start gap-3 rounded-lg border border-border bg-warning-muted px-4 py-3">
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-warning-dark" />
            <div>
              <p className="text-sm font-semibold text-warning-dark">Unsettled expenses</p>
              <p className="text-xs text-warning-dark/80">
                {unsettledCount} expense{unsettledCount > 1 ? "s" : ""} still outstanding — settle balances before
                closing out.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Tab strip ── */}
      <div className="flex gap-1 overflow-x-auto rounded-lg border border-border bg-card p-1">
        {TABS.map(({ value, label, icon: TabIcon }) => {
          const isTabActive = tab === value;
          const count = tabCount(value);
          const warning = tabWarning(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                "relative flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                isTabActive ? "text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {isTabActive && (
                <motion.div
                  layoutId="exp-detail-tab-bg"
                  className="absolute inset-0 rounded-md bg-abyss-500"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <TabIcon className="relative z-10 size-3.5" />
              <span className="relative z-10">{label}</span>
              {count > 0 && (
                <span
                  className={cn(
                    "relative z-10 rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold tabular-nums",
                    warning > 0
                      ? isTabActive
                        ? "bg-white/20 text-white"
                        : "bg-warning-muted text-warning-dark"
                      : isTabActive
                        ? "bg-white/20 text-white"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {warning > 0 ? warning : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          {tab === "overview" && <OverviewTab expedition={exp} expenses={expenses} />}
          {tab === "route" && (
            <RouteTab
              route={exp.route}
              vessels={exp.vessels}
              isActive={isActive}
              // TODO(backend/tracking): pass real coveredNm from VesselPings history
            />
          )}
          {tab === "crew" && (
            <CrewTab
              crew={exp.crew}
              vessels={exp.vessels}
              onApprove={(id) => approveCrewMember.mutate(id)}
              onDecline={(id) => declineCrewMember.mutate(id)}
              onRemove={(id) => removeCrewMember.mutate(id)}
              onUpdateRole={(id, role) => updateCrewRole.mutate({ membershipId: id, role })}
              isMutating={crewMutating}
            />
          )}
          {tab === "expenses" && (
            <ExpensesTab
              expenses={expenses}
              status={exp.status}
              crewCount={confirmedCrewCount}
              onAdd={(input) => recordExpense.mutate(input)}
              onDelete={(id) => deleteExpense.mutate(id)}
              onSettle={(expenseId) => settleExpense.mutate(expenseId)}
              isMutating={expenseMutating}
            />
          )}
          {tab === "documents" && (
            // TODO(backend/documents): replace MOCK_DOCUMENTS with real DocumentsList query
            <DocumentsTab documents={MOCK_DOCUMENTS} />
          )}
          {tab === "log" && (
            <LogTab
              activity={MOCK_ACTIVITY}
              logbookEntries={logbookEntries}
              onAddEntry={(input) => addLogbookEntry.mutate(input)}
              onDeleteEntry={(id) => deleteLogbookEntry.mutate(id)}
              isMutating={logMutating}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
